import { readEntitiesByIDs, watchEntitiesByIDs, readAllEntities, watchAllEntities } from '../server/entity-reads.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { context, serverContext } from '@adornis/baseql/server/context.js';
import { getContextFromRequest } from '@adornis/baseql/server/getContext.js';
import { runInContext } from '@adornis/baseql/server/server.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { getTimeframeByID } from '@adornis/bh-timeframes/api/TimeframeAPI.js';
import { checkAuthorizedMiddleware } from '@adornis/bh-util/middlewares/checkAuthorizedMiddleware.js';
import {
  getCurrentUserServer,
  getCurrentUserServerOrFail,
  loadRawEntityDataByID,
  loadRawEntityDataByIDOrFail,
  loadRawEntityDataByIDs,
} from '@adornis/bh-util/util.js';
import { fileConfig } from '@adornis/file-utils/config.js';
import { AdornisTemplater } from '@adornis/templater/server/index.js';
import { assignRoleToUser } from '@adornis/users/server/roles.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Router } from 'express';
import { mkdir, readFile, writeFile } from 'fs/promises';
import { join } from 'path';
import { defer, switchMap, map, of } from 'rxjs';
import { Institution } from '../entities/institution.js';
import { LecturerContractVariable } from '../entities/LecturerContract.js';
import { LecturerData } from '../entities/lecturerData.js';
import { Product } from '../entities/products.js';
import { Teaching } from '../entities/teaching.js';
import { User } from '../entities/user.js';
import { Workshop } from '../entities/workshop.js';
import { Permission, Role } from '../enums/roles.js';
import { notifyLecturerAddedToProduct } from '../notification/lecturer-added-to-product.js';
import { notifyLecturerRegistration } from '../notification/lecturer-registration.js';
import { mongo } from '../server/mongo.js';
import { notifyUserPermissionChange } from '../server/user-change-notifier.js';
import { toBerlinDate } from '../util.js';
import { getInstitutionByID } from './institution.js';
import { getProductByID } from './product.js';
import { getLecturerContractByID, getLecturerContractFileByID } from './lecturer-contract.js';

async function permittedTeachingProductIDs(lecturerID: string): Promise<string[] | undefined> {
  if (context.serverContext) return undefined;
  const currentUser = await getCurrentUserServerOrFail();
  if (currentUser._id === lecturerID || currentUser.isSuperAdmin) return undefined;

  const teachingCollection = mongo().collection<Teaching>(Teaching._collectionName);
  const teachings = await teachingCollection.find({ lecturerID }, { projection: { productID: 1 } }).toArray();
  const productIDs = [...new Set(teachings.map(teaching => teaching.productID))];
  const products = await loadRawEntityDataByIDs(Product, productIDs, { _id: 1, institutionID: 1 });
  return products
    .filter(product => currentUser.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_LECTURERS, product.institutionID))
    .map(product => product._id);
}

export const subscribeTeachingsForLecturer = registerSubscription({
  operationName: 'subscribeTeachingsForLecturer',
  resolve:
    (skip: number, limit: number, lecturerID: string) =>
    <SelSet extends BaseQLSelectionSet<Teaching>>(gqlFields: SelSet) =>
      defer(() => permittedTeachingProductIDs(lecturerID)).pipe(
        switchMap(productIDs =>
          watchQuery<Teaching>(
            Teaching._collectionName,
            { lecturerID, ...(productIDs ? { productID: { $in: productIDs } } : {}) },
            { skip, limit },
          ),
        ),
      ),
  type: () => [Teaching],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'lecturerID', type: () => ID },
  ],
  middlewares: [checkAuthorizedMiddleware(() => true)],
});

export const subscribeTeachingsForLecturerAndInstitution = registerSubscription({
  operationName: 'subscribeTeachingsForLecturerAndInstitution',
  type: () => [Teaching],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'lecturerID', type: () => ID },
    { name: 'institutionID', type: () => ID },
  ],
  middlewares: [
    checkAuthorizedMiddleware(
      ({ user, params }) =>
        user._id === params.lecturerID ||
        user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_LECTURERS, params.institutionID),
    ),
  ],
  resolve:
    (skip: number, limit: number, lecturerID: string, institutionID: string) =>
    <SelSet extends BaseQLSelectionSet<Teaching>>(gqlFields: SelSet) =>
      watchQuery<Teaching>(Teaching._collectionName, { lecturerID }, { projection: { _id: 1 } }).pipe(
        switchMap(teachings => {
          const rawCol = mongo().collection(Teaching._collectionName);
          return rawCol
            .aggregate([
              { $match: { _id: { $in: teachings.map(teaching => teaching._id) } } },
              {
                $lookup: {
                  from: Product._collectionName,
                  as: 'products',
                  localField: 'productID',
                  foreignField: '_id',
                },
              },
              { $unwind: '$products' },
              {
                $lookup: {
                  from: Institution._collectionName,
                  as: 'institution',
                  localField: 'products.institutionID',
                  foreignField: '_id',
                },
              },
              { $unwind: '$institution' },
              { $match: { 'institution._id': institutionID } },
              { $skip: skip },
              { $limit: limit },
            ])
            .toArray() as Promise<Teaching[]>;
        }),
      ),
});

export const subscribeTeachingsForProduct = registerSubscription({
  operationName: 'subscribeTeachingsForProduct',
  resolve:
    (productID: string) =>
    <SelSet extends BaseQLSelectionSet<Teaching>>(gqlFields: SelSet) =>
      defer(async () => {
        if (context.serverContext) return;
        const currentUser = await getCurrentUserServerOrFail();
        const product = await loadRawEntityDataByIDOrFail(Product, productID, { institutionID: 1 });
        if (currentUser.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_LECTURERS, product.institutionID)) return;
        const teachingCollection = mongo().collection<Teaching>(Teaching._collectionName);
        const ownTeaching = await teachingCollection.findOne({ productID, lecturerID: currentUser._id });
        if (!ownTeaching) throw new ExpectedError('not authorized');
      }).pipe(switchMap(() => watchQuery<Teaching>(Teaching._collectionName, { productID }))),
  type: () => [Teaching],
  params: [{ name: 'productID', type: () => ID }],
  middlewares: [checkAuthorizedMiddleware(() => true)],
});

export const getInstitutionsWithContractsForLecturer = registerQuery({
  operationName: 'getInstitutionsWithContractsForLecturer',
  type: () => [Institution],
  params: [{ name: 'lecturerID', type: () => ID }],
  resolve:
    (lecturerID: string) =>
    async <SelSet extends BaseQLSelectionSet<Institution>>(gqlFields: SelSet) => {
      const currentUser = await getCurrentUserServerOrFail();
      const rawCol = mongo().collection(Teaching._collectionName);
      const institutions = await rawCol.raw
        .aggregate<Institution>([
          { $match: { lecturerID } },
          { $lookup: { from: Product._collectionName, as: 'products', localField: 'productID', foreignField: '_id' } },
          { $unwind: '$products' },
          {
            $lookup: {
              from: Institution._collectionName,
              as: 'institution',
              localField: 'products.institutionID',
              foreignField: '_id',
            },
          },
          { $unwind: '$institution' },
          { $group: { _id: '$institution._id', institution: { $first: '$institution' } } },
          { $replaceRoot: { newRoot: '$institution' } },
        ])
        .toArray();
      if (currentUser._id === lecturerID) return institutions;
      return institutions.filter(institution =>
        currentUser.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_LECTURERS, institution._id),
      );
    },
});

export const promoteUserToLecturer = registerMutation({
  operationName: 'promoteUserToLecturer',
  type: () => Int,
  params: [
    { name: 'userID', type: () => String },
    { name: 'institutionID', type: () => String },
  ],
  resolve: (userID: string, institutionID: string) => async () => {
    // check that institutionID is valid
    const institution = await getInstitutionByID(institutionID)({});
    if (!institution) throw new Error('institution not found ' + institutionID);

    const currentUser = await getCurrentUserServer();
    if (!currentUser?.hasPermission(Permission.EDIT_LECTURERS, institutionID)) {
      throw new Error('You dont have enough permission!');
    }

    await runInContext(serverContext.sessionID, async () => {
      await assignRoleToUser(userID, Role.LECTURER, [institutionID], User as any);
      notifyUserPermissionChange({ userID, kind: 'roles' });
      await notifyLecturerRegistration(userID, institutionID);

      // Send "added to product" mail for each existing teaching in this institution
      const teachingsCol = mongo().collection<Teaching>(Teaching._collectionName);
      const teachings = await teachingsCol.find({ lecturerID: userID }).toArray();
      const productIDs = Array.from(new Set(teachings.map(t => t.productID).filter(Boolean) as string[]));
      const products =
        productIDs.length > 0 ? await loadRawEntityDataByIDs(Product, productIDs, { _id: 1, institutionID: 1 }) : [];
      const productInstitutionByID = new Map<string, string>(
        products.map(p => [(p as any)._id as string, (p as any).institutionID as string]),
      );
      for (const teaching of teachings) {
        if (productInstitutionByID.get(teaching.productID) !== institutionID) continue;
        await notifyLecturerAddedToProduct(userID, teaching.productID);
      }
    });

    return 0;
  },
});

export const createTeaching = registerMutation({
  operationName: 'createTeaching',
  type: () => String,
  params: [{ name: 'teaching', type: () => Teaching }],
  resolve: (teaching: Teaching) => async () => {
    const product = await getProductByID(teaching.productID)({ institutionID: 1 });
    if (!product) throw new Error('No product found for ID: ' + teaching.productID);

    const user = await getCurrentUserServer();
    if (!user?.hasPermission(Permission.EDIT_LECTURERS, product.institutionID)) {
      throw new Error('You dont have enough permission!');
    }

    await teaching.create();
    await runInContext(serverContext.sessionID, async () => {
      await assignRoleToUser(teaching.lecturerID, Role.REFERENT, [product.institutionID], User as any);
      notifyUserPermissionChange({ userID: teaching.lecturerID, kind: 'roles' });
    });

    // Only notify if user is a Lecturer (Referenten don't have a profile)
    const lecturer = await loadRawEntityDataByIDOrFail(User, teaching.lecturerID, { roles: { name: 1, contexts: 1 } });
    const isLecturer = lecturer.roles.some(
      role =>
        role.name === Role.LECTURER && (role.contexts.includes(product.institutionID) || role.contexts.length === 0),
    );
    if (isLecturer) {
      await notifyLecturerAddedToProduct(teaching.lecturerID, teaching.productID);
    }

    return teaching._id;
  },
});

export const createContractFromTeaching = registerMutation({
  operationName: 'createContractFromTeaching',
  resolve: (teachingID: string) => async () => {
    const teaching = await runInContext(serverContext.sessionID, () =>
      getTeachingByID(teachingID)({
        _id: 1,
        _class: 1,
        contractID: 1,
        productID: 1,
        lecturerID: 1,
        lecturer: { _id: 1, firstName: 1, lastName: 1, email: 1 },
        contractPrice: 1,
        contractPriceWords: 1,
        signedContractFileID: 1,
      }),
    );
    if (!teaching) throw new Error('No teaching found for ID: ' + teachingID);
    const authorizationProduct = await getProductByID(teaching.productID)({ institutionID: 1 });
    if (!authorizationProduct) throw new Error('No linked product found for teaching: ' + teachingID);
    if (!context.serverContext) {
      const currentUser = await getCurrentUserServerOrFail();
      const isLecturer = currentUser._id === teaching.lecturerID;
      const mayManageLecturers = currentUser.hasPermission(
        Permission.EDIT_LECTURERS,
        authorizationProduct.institutionID,
      );
      const mayManageContracts = currentUser.hasPermission(
        Permission.MANAGE_CONTRACT_TEMPLATES,
        authorizationProduct.institutionID,
      );
      if (!isLecturer && !mayManageLecturers && !mayManageContracts) throw new ExpectedError('not authorized');
    }
    if (!teaching.contractID) throw new ExpectedError('Für diesen Lehrauftrag ist keine Vertragsvorlage hinterlegt.');

    const lecturerContract = await runInContext(serverContext.sessionID, () =>
      getLecturerContractByID(teaching.contractID!)({ _id: 1, _class: 1, fileID: 1 }),
    );
    if (!lecturerContract) throw new Error('No linked contract found for teaching: ' + teachingID);

    const file = await runInContext(serverContext.sessionID, () =>
      getLecturerContractFileByID(lecturerContract.fileID)({
        _id: 1,
        _class: 1,
        fileID: 1,
        file: { _id: 1, path: 1 },
        meta: { extension: 1 },
      }),
    );
    if (!file) throw new Error('No linked contractFile found for lecturerContract: ' + lecturerContract._id);

    const templater = AdornisTemplater.fromFile(file.getFullPath());

    const product = await getProductByID(teaching.productID)({
      _id: 1,
      _class: 1,
      timeframeID: 1,
      name: 1,
      appointments: { start: 1, end: 1, location: 1 },
    });
    if (!product) throw new Error('No linked product found for teaching: ' + teachingID);

    const timeframe = await getTimeframeByID(product.timeframeID)({ name: 1 });
    // if (!timeframe) throw new Error('No linked timeframe found for product: ' + product._id);

    const lecturerData = await loadRawEntityDataByID(LecturerData, teaching.lecturerID, {
      billingAddress: { streetAndHousenumber: 1, zipCity: { zip: 1, city: 1 }, country: 1 },
    });

    templater.render({
      [LecturerContractVariable.Verguetung_Euro]: teaching.contractPrice,
      [LecturerContractVariable.Verguetung_Ausgeschrieben]: teaching.contractPriceWords,
      [LecturerContractVariable.Vorname]: teaching.lecturer?.firstName,
      [LecturerContractVariable.Nachname]: teaching.lecturer?.lastName,
      [LecturerContractVariable.Anschrift]: lecturerData
        ? `${lecturerData.billingAddress?.streetAndHousenumber}\n${lecturerData.billingAddress?.zipCity?.zip} ${lecturerData.billingAddress?.zipCity?.city}\n${lecturerData.billingAddress?.country}`
        : '',
      [LecturerContractVariable.Produkt_Titel]: product.name,
      [LecturerContractVariable.Produkt_Tag]:
        product instanceof Workshop && product.appointments?.[0]?.start
          ? format(product.appointments[0].start, 'E', { locale: de })
          : '-',
      [LecturerContractVariable.Produkt_Datum]:
        product instanceof Workshop && product.appointments?.[0]?.start
          ? format(toBerlinDate(product.appointments[0].start), 'dd.MM.yyyy')
          : '-',
      [LecturerContractVariable.Produkt_Uhrzeit]:
        product instanceof Workshop && product.appointments?.[0]?.start
          ? format(toBerlinDate(product.appointments[0].start), 'HH:mm')
          : '-',
      [LecturerContractVariable.Semester]: timeframe?.name ?? '',
      [LecturerContractVariable.Datum]: format(toBerlinDate(new Date()), 'dd.MM.yyyy'),
    });
    const pdfBuffer = await templater.toPdfBuffer();
    const dirPath = join(fileConfig.get('datadir'), `/filled-contracts`);
    try {
      await mkdir(dirPath);
    } catch (e) {
      if (!(e as Error).message?.includes('already exists')) throw e;
    }
    await writeFile(join(dirPath, `/${teaching._id}.pdf`), pdfBuffer);
    return `/api/teachings/${teachingID}`;
  },
  type: () => String,
  params: [{ name: 'teachingID', type: () => ID }],
});

export const teachingContractRouter = () => {
  const router = Router();
  router.use('/api/teachings/:teachingID', async (req, res, next) => {
    const teachingID = req.params.teachingID;
    if (!teachingID || !/^[a-f0-9]{24}$/i.test(teachingID)) {
      res.statusCode = 400;
      return res.end('Invalid teaching ID');
    }

    const requestContext = await getContextFromRequest(req);
    if (!requestContext?.userID) return res.sendStatus(401);

    const teaching = await loadRawEntityDataByID(Teaching, teachingID, { lecturerID: 1, productID: 1 });
    if (!teaching) return res.status(404).send('Teaching not found');
    const product = await loadRawEntityDataByID(Product, teaching.productID, { institutionID: 1 });
    if (!product) return res.status(404).send('Product not found');

    const mayReadContract = await runInContext(requestContext.sessionID, async () => {
      const currentUser = await getCurrentUserServerOrFail();
      return (
        currentUser._id === teaching.lecturerID ||
        currentUser.hasPermission(Permission.EDIT_LECTURERS, product.institutionID)
      );
    });
    if (!mayReadContract) return res.sendStatus(403);

    try {
      const fileBuffer = await readFile(join(fileConfig.get('datadir'), `/filled-contracts/${teachingID}.pdf`));
      if (!fileBuffer) {
        res.statusCode = 400;
        return res.end('No contract found for ID: ' + teachingID);
      }

      res.writeHead(200, { 'content-type': 'application/pdf' });
      res.end(fileBuffer);
    } catch (e) {
      res.statusCode = 400;
      return res.end('No contract found for ID: ' + teachingID);
    }
  });
  return router;
};

export const getProductTeachings = registerQuery({
  operationName: 'getProductTeachings',
  type: () => [Teaching],
  params: [{ name: 'productID', type: () => String }],
  resolve:
    (productID: string) =>
    async <TqldFields extends BaseQLSelectionSet<Teaching>>(
      gqlFields: TqldFields,
    ): Promise<NarrowedEntity<Teaching, TqldFields>[]> => {
      const product = await loadRawEntityDataByIDOrFail(Product, productID, { institutionID: 1 });
      const user = await getCurrentUserServer();
      if (!user?.hasPermission(Permission.EDIT_LECTURERS, product.institutionID))
        throw new Error('You dont have enough permission!');
      const result = await loadRawEntityDataByID(Teaching, productID);
      return result as any;
    },
});

export const subscribeTeachingsByUserId = registerSubscription({
  operationName: 'subscribeTeachingsByUserId',
  type: () => [Teaching],
  params: [{ name: 'userID', type: () => String }],
  middlewares: [checkAuthorizedMiddleware(() => true)],
  resolve: (userID: string) => {
    return (gqlFields: BaseQLSelectionSet<Teaching>) => {
      return defer(() => permittedTeachingProductIDs(userID)).pipe(
        switchMap(productIDs =>
          watchQuery<Teaching>(Teaching._collectionName, {
            lecturerID: userID,
            ...(productIDs ? { productID: { $in: productIDs } } : {}),
          }),
        ),
      );
    };
  },
});

export const getTeachingsByLecturerID = registerQuery({
  operationName: 'getTeachingsByLecturerID',
  resolve: (lecturerID: string) => async (gqlFields: BaseQLSelectionSet<Teaching>) => {
    const teachingsForLecturer = await mongo()
      .collection<Teaching>(Teaching._collectionName)
      .find({ lecturerID })
      .toArray();

    const productIDs = await permittedTeachingProductIDs(lecturerID);
    if (!productIDs) return teachingsForLecturer as Teaching[];
    const permittedProductIDs = new Set(productIDs);
    return teachingsForLecturer.filter(teaching => permittedProductIDs.has(teaching.productID)) as Teaching[];
  },
  type: () => [Teaching],
  params: [{ name: 'lecturerID', type: () => String }],
  middlewares: [checkAuthorizedMiddleware(() => true)],
});

export const getLecturerProductTeaching = registerQuery({
  operationName: 'getLecturerProductTeaching',
  resolve: (lecturerID: string, productID: string) => async (gqlFields: BaseQLSelectionSet<Teaching>) => {
    if (!context.serverContext) {
      const currentUser = await getCurrentUserServerOrFail();
      const productCollection = mongo().collection<Product>(Product._collectionName);
      const product = await productCollection.findOne({ _id: productID }, { projection: { institutionID: 1 } });
      if (!product) throw new Error('product not found');
      const isLecturer = currentUser._id === lecturerID;
      const mayViewLecturers = currentUser.hasPermission(
        Permission.VIEW_ADMIN_INSTITUTION_LECTURERS,
        product.institutionID,
      );
      if (!isLecturer && !mayViewLecturers) throw new Error('not authorized');
    }

    const teachingsForLecturer = (await mongo()
      .collection<Teaching>(Teaching._collectionName)
      .find({ lecturerID, productID })
      .toArray()) as Teaching[];

    return teachingsForLecturer;
  },
  type: () => [Teaching],
  params: [
    { name: 'lecturerID', type: () => String },
    { name: 'productID', type: () => String },
  ],
});

export const getTeachingsByIDs = registerQuery({
  operationName: 'getTeachingsByIDs',
  type: () => [Teaching],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<Teaching>>(gqlFields: SelSet) =>
      readEntitiesByIDs(Teaching, ids, gqlFields),
});

export const subscribeTeachingsByIDs = registerSubscription({
  operationName: 'subscribeTeachingsByIDs',
  type: () => [Teaching],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<Teaching>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(Teaching, ids, gqlFields),
});

export const getAllTeachings = registerQuery({
  operationName: 'getAllTeachings',
  type: () => [Teaching],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<Teaching>>(gqlFields: SelSet) =>
      readAllEntities(Teaching, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllTeachings = registerSubscription({
  operationName: 'subscribeAllTeachings',
  type: () => [Teaching],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<Teaching>>(gqlFields: SelSet) =>
      watchAllEntities(Teaching, gqlFields, { skip, limit, excludeIDs }),
});

export const getTeachingByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<Teaching>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getTeachingsByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeTeachingByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<Teaching>>(gqlFields: SelSet) =>
    id ? subscribeTeachingsByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
